import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { object, type ObjectSchema, string } from 'yup';

import { useOnboardingContext } from '@/contexts/onboardingContext';
import useTranslate from '@/hooks/useTranslate';
import DateOfBirthInput from '@/pages/members/pages/Onboarding/components/shared/DateOfBirthInput';
import FirstNameInput from '@/pages/members/pages/Onboarding/components/shared/FirstNameInput';
import LastNameInput from '@/pages/members/pages/Onboarding/components/shared/LastNameInput';
import { STEPS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import { FinalDetailsFormValues } from '@/pages/members/pages/Onboarding/steps/FinalDetailsStep/types';
import StepComponent from '@/pages/members/pages/Onboarding/steps/StepComponent';
import { StepProps } from '@/pages/members/pages/Onboarding/steps/types';
import getSubmittedData from '@/pages/members/pages/Onboarding/utils/getSubmittedData';
import InputGroup from '@/ui/members/components/InputGroup';
import Heading from '@/ui/members/components/typography/Heading/Heading';

const FinalDetailsStep = ({ nodeType, uid }: StepProps) => {
  const onboardingContext = useOnboardingContext();
  const {
    data: { firstName }
  } = onboardingContext;
  const { t } = useTranslate(`${STEPS_PATH_TRANSLATION_KEY}.FinalDetailsStep`);
  const submittedData = getSubmittedData<FinalDetailsFormValues>(
    nodeType,
    uid,
    onboardingContext.steps
  );

  const schema: ObjectSchema<{
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  }> = object({
    firstName: string().required(t('firstNameIsRequired')),
    lastName: string().required(t('lastNameIsRequired')),
    dateOfBirth: string()
      .required(t('dateOfBirthIsRequired'))
      .test('is-valid-date', t('dateOfBirthMustBeValid'), (value) => {
        if (!value) return false;
        const date = new Date(value);
        if (isNaN(date.getTime())) return false;

        // Verify the date didn't roll over to next month (e.g., Feb 30 -> Mar 2, Apr 31 -> May 1)
        // Use UTC methods to avoid timezone conversion issues
        const [year, month, day] = value.split('-');
        if (
          date.getUTCFullYear() !== parseInt(year, 10) ||
          date.getUTCMonth() + 1 !== parseInt(month, 10) ||
          date.getUTCDate() !== parseInt(day, 10)
        ) {
          return false;
        }

        return true;
      })
      .test('is-not-in-future', t('dateOfBirthCannotBeInTheFuture'), (value) => {
        if (!value) return false;
        const date = new Date(value);
        const today = new Date();
        return date <= today;
      })
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<FinalDetailsFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      firstName: submittedData?.firstName || (firstName as string) || '',
      lastName: submittedData?.lastName || '',
      dateOfBirth: submittedData?.dateOfBirth || ''
    },
    resolver: yupResolver(schema)
  });

  return (
    <StepComponent isValid={isValid} uid={uid} nodeType={nodeType} handleSubmit={handleSubmit}>
      <div className="text-center mb-6">
        <Heading className="mb-3" value={1}>
          {t('heading')}
        </Heading>

        <p className="text-base mb-3">{t('subheading')}</p>
      </div>

      <InputGroup>
        <FirstNameInput control={control} fieldError={errors.firstName} name="firstName" />
      </InputGroup>

      <InputGroup>
        <LastNameInput control={control} fieldError={errors.lastName} name="lastName" />
      </InputGroup>

      <InputGroup>
        <DateOfBirthInput control={control} fieldError={errors.dateOfBirth} name="dateOfBirth" />
      </InputGroup>
    </StepComponent>
  );
};

export default FinalDetailsStep;
